import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ChevronLeft,
  EllipsisVertical,
  FileSpreadsheet,
  Lock,
  LogOut,
  AlertTriangle,
  HelpCircle,
  Loader2,
  RefreshCw,
  Coins,
  Edit,
  QrCode,
  Trash2,
  Calendar,
  DollarSign,
  Folder,
  Bell,
  Ticket,
  Languages
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BottomNavigation from "@/components/BottomNavigation";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { authAPI, supportAPI } from "@/api";
import { exportAPI } from "@/api/export";
import { User as UserType } from "@/types";
import useSubscription from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { Capacitor } from "@capacitor/core";
import PurchaseService, { TRIAL_PERIOD_DAYS } from "@/services/PurchaseService";
import { saveCsvToDevice, objectsToCsv } from "@/utils/csvExport";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { currencyAPI } from "@/api/currency";
import CurrencyPreferences from "@/components/CurrencyPreferences";
import { QRCodeSVG } from 'qrcode.react';
import { Progress } from "@/components/ui/progress";
import { subscriptionsAPI } from "@/api/subscriptions";
import { Skeleton } from "@/components/ui/skeleton";
import SubscriptionDebug from "@/components/SubscriptionDebug";
import { NotificationCardWidget } from "@/components/OneSignalNotificationButton";
import LanguageSelector from "@/components/LanguageSelector";
import { useTranslation } from "react-i18next";
import * as Sentry from '@sentry/capacitor';
import { MonoValue } from "@/components/redesign";
import ActionPopup from "@/components/common/ActionPopup";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";

const formSchema = z.object({
  name: z.string().min(1, { message: "İsim alanı boş bırakılamaz" }),
  email: z.string().email({ message: "Geçerli bir e-posta adresi giriniz" }),
  message: z.string().min(10, { message: "Mesajınız en az 10 karakter olmalıdır" }),
});

// Define interface for subscription status details
interface SubscriptionStatusDetails {
  user_id: string;
  plan: string;
  source: string | null;
  ends_at: string | null;
  grace_until: string | null;
  current_time: string | null;
  active_project_count: number;
  passive_project_count: number;
  active_project_limit: number;
  trial_active: boolean;
  projects_locked: boolean;
  // Additional fields from the old interface that might be calculated in the view
  renewal_at?: string | null;
  start_date?: string | null;
  subscription_period_days?: number | null;
  days_remaining?: number | null;
  subscription_progress_percentage?: number | null;
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSupportLoading, setSupportLoading] = useState(false);
  const [isSupportModalOpen, setSupportModalOpen] = useState(false);
  const [isRefreshingRates, setIsRefreshingRates] = useState(false);
  const { user, loading: userLoading, signOut, profile, profileLoading } = useAuth();
  const [isQrModalOpen, setQrModalOpen] = useState(false);
  const [isLanguageModalOpen, setLanguageModalOpen] = useState(false);
  const [isDeleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionStatusDetails | null>(null);
  const [isLoadingSubscriptionDetails, setIsLoadingSubscriptionDetails] = useState(true);

  // Use the subscription context for basic status
  const { status, forceRefreshFromRevenueCat } = useSubscription();
  const {
    isLoading: isSubscriptionLoading,
    planType,
    projectLimit,
    trialDaysRemaining,
    has_access
  } = status;
  const isNativePlatform = Capacitor.isNativePlatform();

  // IMPROVED: Use has_access instead of planType to determine if subscription is expired
  // This ensures we respect the RevenueCat status that we fixed in the context
  const isExpired = has_access === false;
  const isActiveSubscription = has_access === true;

  // Calculate subscription metrics based on start_date and ends_at
  const calculateSubscriptionMetrics = (details: SubscriptionStatusDetails): {
    daysRemaining: number;
    progressPercentage: number;
    totalDays: number;
    elapsedDays: number;
  } => {
    if (!details.ends_at || !details.start_date) {
      return {
        daysRemaining: 0,
        progressPercentage: 0,
        totalDays: 0,
        elapsedDays: 0
      };
    }

    const now = new Date();
    const startDate = new Date(details.start_date);
    const endDate = new Date(details.ends_at);

    // Calculate total subscription period in days
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate days remaining
    const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

    // Calculate elapsed days
    const elapsedDays = Math.max(0, Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));

    // Calculate progress percentage (how much of the subscription period has elapsed)
    const progressPercentage = totalDays > 0 ? Math.min(100, (elapsedDays / totalDays) * 100) : 0;

    return {
      daysRemaining,
      progressPercentage,
      totalDays,
      elapsedDays
    };
  };

  // Function to fetch detailed subscription status - always gets fresh data from database
  const fetchSubscriptionDetails = async (forceRefresh = false) => {
    setIsLoadingSubscriptionDetails(true);
    try {


      // Always get fresh data directly from the database
      const details = await subscriptionsAPI.getSubscriptionStatusDetails();

      // Calculate additional metrics if they're not already provided by the view
      if (details && details.ends_at) {
        const metrics = calculateSubscriptionMetrics(details);
        details.days_remaining = metrics.daysRemaining;
        details.subscription_progress_percentage = metrics.progressPercentage;
        details.subscription_period_days = metrics.totalDays;
      }

      setSubscriptionDetails(details);

    } catch (error) {

      // Show error toast
      toast.error(t('common.error'), {
        description: t('settings.subscription_error'),
      });
    } finally {
      setIsLoadingSubscriptionDetails(false);
    }
  };

  // Fetch subscription details on component mount and refresh the data frequently
  useEffect(() => {
    // Fetch initial live data
    fetchSubscriptionDetails(true);

    // Force refresh subscription status to ensure we have the latest data
    const forceRefreshSubscription = async () => {
      try {

        await forceRefreshFromRevenueCat();
        await fetchSubscriptionDetails(true);
      } catch (error) {

      }
    };

    // Only force refresh if we don't have access but should have it
    if (has_access === false && !isSubscriptionLoading) {
      forceRefreshSubscription();
    }

    // Set up auto-refresh for live data - refresh every 30 seconds when page is visible
    const refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchSubscriptionDetails(true);
      }
    }, 30000); // Refresh every 30 seconds for live data

    return () => clearInterval(refreshInterval);
  }, [has_access, isSubscriptionLoading, forceRefreshFromRevenueCat]);

  // Function to directly check admin status in the database
  const checkAdminStatusDirectly = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
    } catch (e) {

    }
  };

  // Call the function when component mounts
  useEffect(() => {
    if (user) {
      checkAdminStatusDirectly();
    }
  }, [user]);

  const handleLogout = async () => {
    setIsLoading(true);

    try {
      await signOut();
      // The navigation is handled in the AuthContext
    } catch (error) {

      toast.error(t('common.error'), {
        description: t('settings.logout_error'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgradePlan = () => {
    navigate("/subscription");
  };

  const handleRestorePurchases = async () => {
    setIsLoading(true);
    try {
      await PurchaseService.restorePurchases();
      // Refresh subscription details after restoring purchases
      await fetchSubscriptionDetails();
      toast.success(t('settings.restore_success'), {
        description: t('settings.restore_success_desc')
      });
    } catch (error) {
      toast.error(t('common.error'), {
        description: t('settings.restore_error'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: profile?.name || user?.name || "",
      email: profile?.email || user?.email || "",
      message: "",
    },
  });

  // Update form values when user data is loaded
  useEffect(() => {
    if (profile || user) {
      form.reset({
        name: profile?.name || user?.name || "",
        email: profile?.email || user?.email || "",
        message: "",
      });
    }
  }, [profile, user, form]);

  const onSubmitSupport = async (values: z.infer<typeof formSchema>) => {
    setSupportLoading(true);

    try {
      // Call the support API instead of using fetch directly
      const result = await supportAPI.sendSupportRequest(
        values.name,
        values.email,
        values.message,
        user?.id
      );

      if (!result.success) {
        throw new Error('Support request failed');
      }

      // Clear message field after successful submission
      form.setValue('message', '');
      setSupportModalOpen(false);

      toast.success(t('settings.support_sent'), {
        description: t('settings.support_sent_desc'),
      });
    } catch (error) {
      toast.error(t('common.error'), {
        description: t('settings.support_error'),
      });
    } finally {
      setSupportLoading(false);
    }
  };

  // Handle Excel export
  const handleExcelExport = async () => {
    if (isLoading) return; // Prevent multiple clicks

    setIsLoading(true);

    try {
      // Show initial loading toast
      toast.dismiss();
      toast.message(t('settings.export_preparing'), {
        description: t('settings.export_preparing_desc'),
        duration: 8000, // Use a reasonable duration instead of Infinity
      });

      // Fetch all user data
      const allData = await exportAPI.getAllUserData();


      // Update toast to show we're now exporting
      toast.dismiss();
      toast.message(t('settings.export_creating'), {
        description: t('settings.export_creating_desc'),
        duration: 8000,
      });

      // Create a date string for filenames
      const date = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');

      // Create CSV files for each data type
      let exportedFiles = 0;

      // Projects CSV
      if (allData.projects?.length > 0) {
        try {
          const projectsCsv = objectsToCsv(allData.projects, [
            { key: 'name', label: 'Proje Adı' },
            { key: 'status', label: 'Durum' },
            { key: 'location', label: 'Konum' },
            { key: 'start_date', label: 'Başlangıç Tarihi' },
            { key: 'end_date', label: 'Bitiş Tarihi' },
            { key: 'progress', label: 'Tamamlanma Yüzdesi' },
            { key: 'client', label: 'Müşteri' },
            { key: 'budget', label: 'Bütçe' },
            { key: 'id', label: 'ID' }
          ]);

          // Save projects CSV
          const filename = `santiyex-projeler-${date}.csv`;
          const result = await saveCsvToDevice({
            filename,
            csvData: projectsCsv
          });

          if (result.success) {
            exportedFiles++;

          }
        } catch (projectError) {

        }
      }

      // Entries CSV
      if (allData.entries?.length > 0) {
        try {
          const entriesCsv = objectsToCsv(allData.entries, [
            { key: 'id', label: 'ID' },
            { key: 'type', label: 'Tür' },
            { key: 'date', label: 'Tarih' },
            { key: 'project_id', label: 'Proje ID' },
            { key: 'description', label: 'Açıklama' },
            { key: 'original_amount', label: 'Tutar' },
            { key: 'original_currency', label: 'Para Birimi' },
            { key: 'person_id', label: 'Kişi ID' },
            { key: 'image_url', label: 'Görsel URL' },
            { key: 'due_date', label: 'Vade Tarihi' },
            { key: 'is_official', label: 'Resmi İşlem' }
          ]);

          // Save entries CSV
          const filename = `santiyex-kayitlar-${date}.csv`;
          const result = await saveCsvToDevice({
            filename,
            csvData: entriesCsv
          });

          if (result.success) {
            exportedFiles++;

          }
        } catch (entriesError) {

        }
      }

      // People CSV
      if (allData.people?.length > 0) {
        try {
          const peopleCsv = objectsToCsv(allData.people, [
            { key: 'id', label: 'ID' },
            { key: 'name', label: 'Ad Soyad' },
            { key: 'category', label: 'Kategori' },
            { key: 'specialty', label: 'Uzmanlık' },
            { key: 'phone', label: 'Telefon' },
            { key: 'email', label: 'E-posta' }
          ]);

          // Save people CSV
          const filename = `santiyex-kisiler-${date}.csv`;
          const result = await saveCsvToDevice({
            filename,
            csvData: peopleCsv
          });

          if (result.success) {
            exportedFiles++;

          }
        } catch (peopleError) {

        }
      }

      // Materials CSV
      if (allData.materials?.length > 0) {
        try {
          const materialsCsv = objectsToCsv(allData.materials, [
            { key: 'id', label: 'ID' },
            { key: 'name', label: 'Malzeme Adı' },
            { key: 'brand', label: 'Marka' },
            { key: 'quantity', label: 'Miktar' },
            { key: 'unit', label: 'Birim' },
            { key: 'area', label: 'Alan' },
            { key: 'details', label: 'Detaylar' },
            { key: 'project_id', label: 'Proje ID' }
          ]);

          // Save materials CSV
          const filename = `santiyex-malzemeler-${date}.csv`;
          const result = await saveCsvToDevice({
            filename,
            csvData: materialsCsv
          });

          if (result.success) {
            exportedFiles++;

          }
        } catch (materialsError) {

        }
      }

      // Construction Stages CSV
      if (allData.constructionStages?.length > 0) {
        try {
          const stagesCsv = objectsToCsv(allData.constructionStages, [
            { key: 'id', label: 'ID' },
            { key: 'name', label: 'Aşama Adı' },
            { key: 'phase', label: 'Faz' },
            { key: 'completed', label: 'Tamamlandı' },
            { key: 'completed_date', label: 'Tamamlanma Tarihi' },
            { key: 'note', label: 'Not' },
            { key: 'project_id', label: 'Proje ID' }
          ]);

          // Save construction stages CSV
          const filename = `santiyex-insaat-asamalari-${date}.csv`;
          const result = await saveCsvToDevice({
            filename,
            csvData: stagesCsv
          });

          if (result.success) {
            exportedFiles++;

          }
        } catch (stagesError) {

        }
      }

      // Reports CSV
      if (allData.reports?.length > 0) {
        try {
          const reportsCsv = objectsToCsv(allData.reports, [
            { key: 'id', label: 'ID' },
            { key: 'type', label: 'Rapor Türü' },
            { key: 'project_id', label: 'Proje ID' },
            { key: 'start_date', label: 'Başlangıç Tarihi' },
            { key: 'end_date', label: 'Bitiş Tarihi' },
            { key: 'include_photos', label: 'Fotoğraflar Dahil' },
            { key: 'created_at', label: 'Oluşturulma Tarihi' }
          ]);

          // Save reports CSV
          const filename = `santiyex-raporlar-${date}.csv`;
          const result = await saveCsvToDevice({
            filename,
            csvData: reportsCsv
          });

          if (result.success) {
            exportedFiles++;

          }
        } catch (reportsError) {

        }
      }

      // Check if we exported any files and update the final toast
      toast.dismiss();
      if (exportedFiles > 0) {
        toast.success(t('settings.export_success'), {
          description: t('settings.export_success_desc', { count: exportedFiles }),
          duration: 3000,
        });
      } else {
        toast.message(t('settings.export_completed'), {
          description: t('settings.export_no_data'),
          duration: 3000,
        });
      }
    } catch (error) {


      toast.dismiss();
      toast.error(t('common.error'), {
        description: t('settings.export_error'),
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refresh exchange rates
  const handleRefreshExchangeRates = async () => {
    try {
      setIsRefreshingRates(true);

      // Show a loading toast
      toast(t('settings.rates_updating'));


      const result = await currencyAPI.forceRefreshRates();


      if (result.data) {
        toast.success(t('settings.rates_updated', { count: result.data.length }));
      } else {
        toast.error(t('settings.rates_update_error', { error: result.error?.message || t('common.unknown_error') }));
      }
    } catch (error) {

      toast.error(t('settings.rates_error'));
    } finally {
      setIsRefreshingRates(false);
    }
  };

  const [isCurrencyModalOpen, setCurrencyModalOpen] = useState(false);
  const { preferences } = useUserPreferences();
  const selectedCurrencySummary = [
    preferences?.base_currency || "TRY",
    preferences?.second_currency || "USD",
    preferences?.third_currency || "EUR",
  ].join(" • ");
  const currentLanguageLabel =
    i18n.language?.startsWith("tr") ? "TURKCE" :
    i18n.language?.startsWith("en") ? "ENGLISH" :
    i18n.language?.startsWith("de") ? "DEUTSCH" :
    i18n.language?.startsWith("fr") ? "FRANCAIS" :
    i18n.language?.startsWith("ru") ? "RUSSIAN" :
    i18n.language?.startsWith("ar") ? "ARABIC" :
    i18n.language?.startsWith("zh") ? "CHINESE" :
    i18n.language?.startsWith("ja") ? "JAPANESE" :
    "TURKCE";
  const headerName = profile?.name || user?.name || t('settings.user');
  const headerCompany = profile?.company?.trim() || '-';
  const headerCity = profile?.city?.trim() || profile?.country?.trim() || '-';
  const headerPhone = profile?.phone || user?.phone;

  // Handle account deletion
  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);

    // Show initial progress toast
    const deletionToastId = toast.loading(t('settings.account_deleting'), {
      description: t('settings.account_deleting_desc'),
    });

    try {
      // Update progress
      toast.loading(t('settings.data_deleting'), {
        id: deletionToastId,
        description: t('settings.data_deleting_desc'),
      });

      // Delete the account from the database with comprehensive deletion
      const result = await authAPI.deleteAccount();

      if (result.success) {
        // Update progress
        toast.loading(t('settings.cache_clearing'), {
          id: deletionToastId,
          description: t('settings.cache_clearing_desc'),
        });

        // Account deleted successfully - now perform complete cleanup


        // Clear additional cache items specific to account deletion


        // Clear all localStorage items that might contain user data
        localStorage.removeItem('santiyex-session-expiry');
        localStorage.removeItem('santiyex-user-cache');
        localStorage.removeItem('santiyex-user-lock-status');
        localStorage.removeItem('santiyex-subscription-cache');
        localStorage.removeItem('faceIDEmail');
        localStorage.removeItem('face-id-login-redirect');
        localStorage.removeItem('santiyex-login-success');
        localStorage.removeItem('santiyex-prompt-faceid');

        // Clear any FaceID password keys that might exist
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(key => {
          if (key.startsWith('faceID_pwd_')) {
            localStorage.removeItem(key);
          }
        });

        // No need to clear sessionStorage as we're not using it anymore for performance reasons

        // Clear Capacitor Preferences on native platforms
        if (Capacitor.isNativePlatform()) {
          try {
            // Update progress
            toast.loading(t('settings.mobile_clearing'), {
              id: deletionToastId,
              description: t('settings.mobile_clearing_desc'),
            });

            const { Preferences } = await import('@capacitor/preferences');

            // Clear Supabase auth token and other Capacitor stored data
            await Preferences.remove({ key: 'santiyex-auth-token' });

            // Clear any other Capacitor stored preferences if they exist
            // This is a safe operation that won't fail if keys don't exist
            const keysToRemove = [
              'onesignal-external-id',
              'onesignal-user-id',
              'face-id-credentials',
              'user-preferences',
              'app-settings'
            ];

            for (const key of keysToRemove) {
              try {
                await Preferences.remove({ key });
              } catch (prefError) {
                // Ignore individual key removal errors
              }
            }


          } catch (capacitorError) {

          }
        }

        // Perform additional cleanup for RevenueCat and OneSignal
        if (Capacitor.isNativePlatform()) {
          try {
            // Update progress
            toast.loading(t('settings.services_clearing'), {
              id: deletionToastId,
              description: t('settings.services_clearing_desc'),
            });


            const PurchaseService = (await import('@/services/PurchaseService')).default;
            await PurchaseService.logoutFromRevenueCat();
          } catch (rcError) {

          }

          try {
            const { enhancedOneSignalLogout } = await import('@/integrations/onesignal/enhanced-auth-flow');
            await enhancedOneSignalLogout({
              silentMode: true,
              timeout: 3000,
              showToast: (options: any) => toast(options) // ✅ FIX: Pass toast hook as callback
            });
          } catch (osError) {

          }
        }



        // Final success toast
        toast.success(t('settings.account_deleted'), {
          id: deletionToastId,
          description: t('settings.account_deleted_desc'),
          duration: 3000,
        });

        // Navigate to login page with replace to prevent going back
        navigate("/login", { replace: true });

        // Force a page reload after a short delay to ensure all cached data is cleared
        setTimeout(() => {

          window.location.reload();
        }, 1000);

      } else {
        // Dismiss loading toast and show error
        toast.dismiss(deletionToastId);

        // Provide more specific error information
        const errorMessage = result.error?.message || "Bilinmeyen hata";


        toast.error(t('settings.account_delete_failed'), {
          description: t('settings.account_delete_failed_desc', { error: errorMessage }),
          duration: 5000,
        });
      }
    } catch (error) {
      // Dismiss loading toast and show error
      toast.dismiss(deletionToastId);


      toast.error(t('settings.critical_error'), {
        description: t('settings.critical_error_desc'),
        duration: 5000,
      });
    } finally {
      setIsDeletingAccount(false);
      setDeleteAccountModalOpen(false);
    }
  };

  // Sentry test function
  const testSentryError = () => {
    try {
      // This snippet contains intentional errors as suggested by Sentry docs


      // Test 1: Undefined function call
      (window as any).myUndefinedFunction();

    } catch (error) {


      // Manually capture the error to ensure it's sent to Sentry
      Sentry.captureException(error);

      toast.success(t('settings.sentry_test_success'), {
        description: t('settings.sentry_test_desc'),
      });
    }

    // Test 2: Manual error capture
    try {
      throw new Error("Bu manuel Sentry test hatasıdır - endişelenmenize gerek yok!");
    } catch (manualError) {
      Sentry.captureException(manualError);

    }
  };

  // Reusable section card wrapper
  const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <div className={`bg-white border border-brand-100 rounded-[10px] shadow-[0_2px_2px_rgba(10,13,18,0.06),0_4px_4px_rgba(10,13,18,0.1)] overflow-hidden ${className ?? ""}`}>
      {children}
    </div>
  );

  // Reusable section title
  const SectionTitle: React.FC<{ label: string }> = ({ label }) => (
    <p className="px-2 pt-2 pb-2 text-[14px] leading-5 font-extrabold uppercase text-brand-800 md:pt-3">
      {label}
    </p>
  );

  // Reusable row
  const SettingsRow: React.FC<{
    icon: React.ReactNode;
    label: string;
    value?: React.ReactNode;
    description?: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    isFirst?: boolean;
    isLast?: boolean;
  }> = ({ icon, label, value, description, onClick, disabled, isFirst, isLast }) => (
    <button
      type="button"
      className="w-full min-h-[50px] px-3.5 py-2.5 text-left disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between"
      onClick={onClick}
      disabled={disabled}
    >
      <div className="flex w-full items-start gap-2 min-w-0">
        <span className="shrink-0 text-brand-600 mt-0.5">{icon}</span>
        <span className="min-w-0">
          <span className="block text-[14px] leading-5 font-extrabold text-brand-600 truncate">{label}</span>
          {description && (
            <span className="block text-[12px] leading-[16.5px] font-semibold text-gray-500 break-words">{description}</span>
          )}
        </span>
      </div>
      <div className="flex items-center gap-1.5 self-end sm:ml-2 sm:self-auto shrink-0">
        {value && <span className="text-[12px] leading-[16.5px] font-semibold uppercase text-gray-500 max-w-[65vw] sm:max-w-[38vw] md:max-w-[320px] truncate">{value}</span>}
        {onClick && <ChevronRight className="h-4 w-4 text-brand-600" />}
      </div>
    </button>
  );

  return (
    <div className="page-container bg-[#f5f5f5]">
      <main className="main-content !pt-0">
      <div className="sticky top-0 z-20 bg-brand-900 rounded-b-[24px] shadow-[0_2px_2px_rgba(16,24,40,0.06),0_4px_4px_rgba(16,24,40,0.1)] px-4 sm:px-5 pt-[calc(var(--safe-area-top,0px)+0.5rem)] pb-5">
        <div className="w-full max-w-[980px] mx-auto">
          <div className="h-[60px] md:h-16 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-9 px-2 rounded-lg text-brand-400 inline-flex items-center gap-1.5"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="text-[15px] font-semibold">Geri</span>
            </button>
            <h1 className="text-[18px] font-bold leading-[24px] text-white">{t('settings.title')}</h1>
            <button type="button" className="h-9 w-9 rounded-lg text-brand-400 inline-flex items-center justify-center">
              <EllipsisVertical className="h-5 w-5" />
            </button>
          </div>

          <div className="pt-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] leading-[19.5px] font-medium text-white/70">
                  {t('settings.company_info', { defaultValue: 'Sirket Bilgileri' })}
                </p>
                <p className="mt-0.5 text-[clamp(1.9rem,7vw,2.125rem)] leading-[1] font-bold tracking-[-0.68px] text-white truncate">
                  {headerName}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setQrModalOpen(true)}
                className="h-[38px] px-3 rounded-lg text-brand-400 text-[14px] font-semibold shrink-0"
              >
                {t('common.share', { defaultValue: 'Paylas' })}
              </button>
            </div>
            <p className="mt-3 text-[16px] leading-6 text-white truncate">{headerCompany}</p>
            <p className="text-[14px] leading-5 font-medium text-white truncate">{headerCity}</p>
            {headerPhone && (
              <p className="text-[14px] leading-5 font-medium text-white truncate">{headerPhone}</p>
            )}
          </div>
        </div>
      </div>

        <div className="px-4 sm:px-5 md:px-6 pb-6 pt-[14px]">
          {userLoading || profileLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            </div>
          ) : (
            <div className="w-full max-w-[980px] mx-auto">
              <div className="w-full max-w-[344px] md:max-w-[560px] lg:max-w-[680px] mx-auto space-y-[14px]">
              {/* Profile Section */}
              <SectionTitle label={t('settings.account_section', { defaultValue: 'Hesap' })} />
              <SectionCard className="mb-1">
                <div className="flex items-start justify-between px-[14px] py-4 gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-[18px] leading-[normal] font-bold text-brand-900 truncate">
                      {profile?.name || user?.name || t('settings.user')}
                    </p>
                    {profile?.selected_role && (
                      <p className="text-[14px] leading-5 font-extrabold text-[#3b608f] mt-1 capitalize">
                        {profile.selected_role === 'employer' ? t('roles.employer') :
                         profile.selected_role === 'laborer' ? t('roles.laborer') :
                         profile.selected_role === 'supplier' ? t('roles.supplier') :
                         profile.selected_role === 'architect' ? t('roles.architect') :
                         profile.selected_role === 'engineer' ? t('roles.engineer') :
                         t('roles.other')}
                      </p>
                    )}
                    <p className="text-[12px] leading-[16.5px] font-semibold text-gray-500 truncate mt-1">
                      {profile?.email || user?.email || t('settings.no_email')}
                    </p>
                    {(profile?.phone || user?.phone) && (
                      <p className="text-[12px] leading-[16.5px] font-semibold text-gray-500 truncate">
                        {profile?.phone || user?.phone}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/account-settings')}
                    className="h-6 px-2 text-brand-800 shrink-0 text-[14px] font-semibold"
                    title={t('settings.edit_account')}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    {t('settings.edit_account', { defaultValue: 'Duzenle' })}
                  </Button>
                </div>
              </SectionCard>

              {/* Subscription Section */}
              <SectionTitle label={t('settings.subscription_section', { defaultValue: 'Abonelik' })} />
              <SectionCard className="mb-1">
                <div className="px-4 pt-4 pb-2">
                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-[15px] font-semibold text-gray-900">{t('settings.current_plan')}</span>
                    <div className="flex items-center gap-1.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => fetchSubscriptionDetails(true)}
                        className="h-7 w-7 text-gray-400"
                        disabled={isLoadingSubscriptionDetails}
                        title={t('settings.refresh_subscription')}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${isLoadingSubscriptionDetails ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          try {
                            await forceRefreshFromRevenueCat();
                            await fetchSubscriptionDetails(true);
                          } catch (error) {}
                        }}
                        className="h-7 w-7 text-warning-600"
                        disabled={isSubscriptionLoading}
                        title={t('settings.sync_revenuecat')}
                      >
                        <Coins className={`h-3.5 w-3.5 ${isSubscriptionLoading ? 'animate-spin' : ''}`} />
                      </Button>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                        isExpired
                          ? 'bg-error-50 text-error-600'
                          : 'bg-brand-50 text-brand-600'
                      }`}>
                        {isLoadingSubscriptionDetails || isSubscriptionLoading ? t('common.loading') :
                        isActiveSubscription ? (
                          subscriptionDetails?.plan === 'premium' ? t('settings.premium_active') :
                          subscriptionDetails?.plan === 'elite' ? t('settings.elite_active') :
                          t('settings.free')
                        ) :
                        t('settings.expired')}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {isLoadingSubscriptionDetails ? (
                      <Skeleton className="h-4 w-28" />
                    ) : subscriptionDetails?.plan === 'premium' ? (
                      <div className="flex items-center gap-2 text-[13px] text-gray-600">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <MonoValue
                          value={749}
                          suffix="₺"
                          format
                          size="sm"
                          tone="default"
                        />
                        <span className="text-gray-500">{t('settings.per_month')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-[13px] text-gray-600">
                        <span className="text-[15px] font-bold text-gray-900">{t('settings.free')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-[13px] text-gray-600">
                      <Folder className="h-4 w-4 text-gray-400" />
                      <span>{subscriptionDetails?.active_project_limit || projectLimit} {t('settings.projects')}</span>
                    </div>
                  </div>

                  {/* Promo Code Warning */}
                  {subscriptionDetails?.source === 'redeem_code' && (
                    <div className="mb-3">
                      <div className="bg-warning-50 border border-warning-300 rounded-xl p-3">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning-600 mt-0.5 shrink-0" />
                          <div className="text-[12px]">
                            <p className="font-semibold text-warning-600 mb-0.5">{t('settings.important_reminder')}</p>
                            <p className="text-warning-600">{t('settings.promo_code_warning')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subscription Progress */}
                  {(!isExpired || isActiveSubscription) && subscriptionDetails?.ends_at && (
                    <div className="mb-3">
                      {(() => {
                        const now = new Date();
                        const endDate = new Date(subscriptionDetails.ends_at);
                        const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                        const totalDays = subscriptionDetails.subscription_period_days || TRIAL_PERIOD_DAYS;
                        const progressPercentage = subscriptionDetails.subscription_progress_percentage ||
                          ((totalDays - daysRemaining) / totalDays * 100);

                        const isTrialSubscription = subscriptionDetails.trial_active || planType === 'trial';
                        const subscriptionLabel = isTrialSubscription ? t('settings.trial_period') : t('settings.subscription_period');

                        const urgency = daysRemaining <= 3 ? 'error' : daysRemaining <= 7 ? 'warning' : 'ok';

                        return (
                          <div className="space-y-1.5">
                            <div className="flex justify-between text-[11px] text-gray-500">
                              <span className="font-semibold uppercase tracking-[0.04em]">{subscriptionLabel}</span>
                              <MonoValue
                                value={daysRemaining}
                                suffix={` ${t('settings.days_remaining')}`}
                                size="meta"
                                tone={urgency === 'error' ? 'error' : 'muted'}
                              />
                            </div>
                            <div className={`relative h-2 w-full overflow-hidden rounded-full ${
                              urgency === 'error' ? 'bg-error-50' : urgency === 'warning' ? 'bg-warning-50' : 'bg-brand-50'
                            }`}>
                              <div
                                className={`h-full transition-all rounded-full ${
                                  urgency === 'error' ? 'bg-error-600' : urgency === 'warning' ? 'bg-warning-600' : 'bg-brand-600'
                                }`}
                                style={{ width: `${Math.min(100, progressPercentage)}%` }}
                              />
                            </div>
                            <div className="flex justify-between text-[11px] text-gray-400 mt-0.5">
                              <MonoValue
                                value={subscriptionDetails?.start_date ? new Date(subscriptionDetails.start_date).toLocaleDateString('tr-TR') : ''}
                                size="meta"
                                tone="muted"
                              />
                              <MonoValue
                                value={new Date(subscriptionDetails.ends_at).toLocaleDateString('tr-TR')}
                                size="meta"
                                tone="muted"
                              />
                            </div>

                            {subscriptionDetails?.source === 'redeem_code' && (
                              <div className="flex items-center justify-center mt-1">
                                <Ticket className="h-3 w-3 mr-1 text-success-600" />
                                <span className="text-[11px] text-success-600 font-semibold">
                                  {t('settings.activated_with_promo')}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      <div className="mt-2 flex flex-wrap gap-y-1 gap-x-4 text-[11px] text-gray-400">
                        {subscriptionDetails?.renewal_at && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3 text-success-600" />
                            <span>{t('settings.renewal')} </span>
                            <MonoValue
                              value={new Date(subscriptionDetails.renewal_at).toLocaleDateString('tr-TR')}
                              size="meta"
                              tone="muted"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="pb-4 flex flex-col gap-2">
                    {(isExpired && !isActiveSubscription) ? (
                      <Button onClick={handleUpgradePlan} className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-lg">
                        {t('settings.upgrade_plan')}
                      </Button>
                    ) : (
                      <Button onClick={() => navigate("/plan-details")} variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg">
                        {t('settings.plan_details')}
                      </Button>
                    )}

                    {isNativePlatform && (
                      <Button onClick={handleRestorePurchases} variant="outline" className="w-full border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg" disabled={isLoading}>
                        {isLoading ? (
                          <span className="flex items-center">
                            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2"></span>
                            {t('settings.processing')}
                          </span>
                        ) : (
                          <>{t('settings.restore_purchases')}</>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </SectionCard>

              {/* Preferences Section */}
              <SectionTitle label={t('settings.preferences_section', { defaultValue: 'Tercihler' })} />
              <SectionCard className="space-y-2 p-0.5">
                <SettingsRow
                  icon={<Coins className="h-4 w-4 text-success-600" />}
                  label={t('settings.currency_settings')}
                  value={selectedCurrencySummary}
                  onClick={() => setCurrencyModalOpen(true)}
                />
                <SettingsRow
                  icon={<Languages className="h-4 w-4 text-brand-500" />}
                  label={t('settings.language_selection')}
                  value={currentLanguageLabel}
                  onClick={() => setLanguageModalOpen(true)}
                />
              </SectionCard>

              {/* Notifications Section */}
              <SectionTitle label={t('settings.notifications_section')} />
              <SectionCard className="mb-1">
                <div className="px-4 py-3">
                  <NotificationCardWidget />
                </div>
              </SectionCard>

              {/* Sharing Section */}
              <SectionTitle label={t('settings.sharing')} />
              <SectionCard>
                <SettingsRow
                  icon={<QrCode className="h-4 w-4 text-brand-500" />}
                  label={t('settings.share_with_qr')}
                  onClick={() => setQrModalOpen(true)}
                />
              </SectionCard>

              {/* Security Section */}
              <SectionTitle label={t('settings.security_section')} />
              <SectionCard className="space-y-2 p-0.5">
                <SettingsRow
                  icon={<Lock className="h-4 w-4 text-brand-600" />}
                  label={t('settings.change_password')}
                  onClick={() => navigate("/change-password")}
                />
              </SectionCard>

              {/* Admin Panel Section - Only visible to admins */}
              {user && 'is_dev_admin' in user && user.is_dev_admin && (
                <>
                  <SectionTitle label={t('settings.developer')} />
                  <SectionCard>
                    <SettingsRow
                      icon={
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4 text-purple-500">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
                        </svg>
                      }
                      label={t('settings.admin_panel')}
                      onClick={() => navigate("/admin")}
                    />
                    <div className="border-t border-gray-200" />
                    <SettingsRow
                      icon={<RefreshCw className="h-4 w-4 text-brand-500" />}
                      label={t('settings.refresh_exchange_rates')}
                      onClick={handleRefreshExchangeRates}
                      disabled={isRefreshingRates}
                    />
                    <div className="border-t border-gray-200" />
                    <SettingsRow
                      icon={<Bell className="h-4 w-4 text-warning-600" />}
                      label={t('settings.test_notifications')}
                      onClick={() => navigate("/test-notifications")}
                    />
                    <div className="border-t border-gray-200" />
                    <SettingsRow
                      icon={<AlertTriangle className="h-4 w-4 text-error-600" />}
                      label={t('settings.sentry_test')}
                      onClick={testSentryError}
                    />
                  </SectionCard>
                </>
              )}

              {/* Data Management Section */}
              <SectionTitle label={t('settings.data_management')} />
              <SectionCard className="mb-1">
                <SettingsRow
                  icon={<FileSpreadsheet className="h-4 w-4 text-brand-600" />}
                  label={t('settings.export_data_csv')}
                  description={t('settings.notification_channels', { defaultValue: 'Push, e-posta, uygulama ici' })}
                  onClick={handleExcelExport}
                  disabled={isLoading}
                />
              </SectionCard>

              {/* Support Section */}
              <SectionTitle label={t('settings.support_section')} />
              <SectionCard className="mb-1">
                <SettingsRow
                  icon={<HelpCircle className="h-4 w-4 text-brand-500" />}
                  label={t('settings.get_support')}
                  onClick={() => setSupportModalOpen(true)}
                />
              </SectionCard>

              {/* Logout Section */}
              <div className="pt-2">
                <Button
                  variant="outline"
                  className="w-full h-[50px] rounded-[10px] border-[#ffd8d5] text-[#ff5449] bg-white shadow-[0_2px_2px_rgba(10,13,18,0.06),0_4px_4px_rgba(10,13,18,0.1)] font-extrabold"
                  onClick={() => setLogoutModalOpen(true)}
                  disabled={isLoading}
                >
                  <LogOut className="mr-2 h-4 w-4 text-[#ff5449]" />
                  {isLoading ? t('auth.logging_out') : t('auth.logout')}
                </Button>
              </div>

              {/* Delete Account Section */}
              <div className="pb-6">
                <Button
                  variant="outline"
                  className="w-full h-[50px] rounded-[10px] border-[#ffd8d5] text-[#ff5449] bg-white shadow-[0_2px_2px_rgba(10,13,18,0.06),0_4px_4px_rgba(10,13,18,0.1)] font-extrabold"
                  onClick={() => setDeleteAccountModalOpen(true)}
                  disabled={isDeletingAccount}
                >
                  <Trash2 className="mr-2 h-4 w-4 text-[#ff5449]" />
                  {isDeletingAccount ? t('settings.deleting_account') : t('settings.delete_account')}
                </Button>
              </div>
              </div>
            </div>
          )}
        </div>

        {/* Development Debug Panel - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="px-4 pb-4">
            <SubscriptionDebug />
          </div>
        )}
      </main>

      <BottomNavigation />

      <ActionPopup
        isOpen={isLogoutModalOpen}
        icon={<LogOut className="h-5 w-5" />}
        title={t("settings.logout_confirm_title", { defaultValue: "Cikis yap" })}
        description={t("settings.logout_confirm_desc", { defaultValue: "Hesaptan cikmak istediginize emin misiniz?" })}
        cancelLabel={t("settings.cancel", { defaultValue: "Vazgec" })}
        confirmLabel={t("auth.logout", { defaultValue: "Cikis Yap" })}
        confirmTone="brand"
        loading={isLoading}
        loadingLabel={t("settings.processing")}
        onCancel={() => setLogoutModalOpen(false)}
        onConfirm={async () => {
          await handleLogout();
          setLogoutModalOpen(false);
        }}
      />

      <ActionPopup
        isOpen={isDeleteAccountModalOpen}
        icon={<Trash2 className="h-5 w-5" />}
        iconTone="danger"
        title={t("settings.delete_account_confirm_title", { defaultValue: "Hesabi Sil" })}
        description={
          <>
            <p>{t("settings.delete_irreversible", { defaultValue: "Bu islem geri alinamaz." })}</p>
            <p className="font-semibold text-[#414651]">
              {t("settings.delete_warning", { defaultValue: "Silmek istediginize emin misiniz?" })}
            </p>
          </>
        }
        cancelLabel={t("settings.cancel", { defaultValue: "Vazgec" })}
        confirmLabel={t("common.delete", { defaultValue: "Sil" })}
        confirmTone="danger"
        loading={isDeletingAccount}
        loadingLabel={t("settings.processing")}
        onCancel={() => {
          if (!isDeletingAccount) setDeleteAccountModalOpen(false);
        }}
        onConfirm={handleDeleteAccount}
      />

      {/* Support Modal */}
      <Modal
        isOpen={isSupportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        title={t('settings.support_request')}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmitSupport)} className="space-y-4 pb-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.full_name')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('settings.your_name')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.your_email')}</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={t('settings.your_email')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('settings.your_message')}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('settings.message_placeholder')}
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={isSupportLoading} className="bg-brand-600 hover:bg-brand-700 text-white rounded-lg">
                {isSupportLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('settings.sending')}
                  </>
                ) : (
                  t('settings.send')
                )}
              </Button>
            </div>
          </form>
        </Form>
      </Modal>

      {/* QR Code Modal */}
      <Modal
        isOpen={isQrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title={t('settings.qr_share_title')}
      >
        <div className="p-6 flex flex-col items-center justify-center">
          {user && user.id ? (
            <>
              <div className="p-4 bg-white rounded-xl shadow-sm border border-gray-200">
                <QRCodeSVG
                  value={user.id}
                  size={200}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="mt-4 text-[13px] text-center text-gray-500">
                {t('settings.qr_instruction')}
              </p>
            </>
          ) : (
            <p className="text-center text-gray-500">
              {t('settings.user_info_error')}
            </p>
          )}
        </div>
      </Modal>

      {/* Currency Preferences Modal */}
      <Modal
        isOpen={isCurrencyModalOpen}
        onClose={() => setCurrencyModalOpen(false)}
        title={t('settings.currency_preferences')}
      >
        <div className="p-4">
          <CurrencyPreferences />
        </div>
      </Modal>

      {/* Language Preferences Modal */}
      <Modal
        isOpen={isLanguageModalOpen}
        onClose={() => setLanguageModalOpen(false)}
        title={t('settings.language_preferences')}
      >
        <div className="p-4">
          <LanguageSelector showIcon={false} showLabel={false} />
        </div>
      </Modal>

    </div>
  );
};

export default SettingsPage;
